import {
    useEffect,
    useState,
} from 'react';
import PDFViewerWithZoom from './PDFViewerWithZoom';
import OfficeViewer from 'builders/builderInternal/components/AITeacher/Viewer/OfficeViewer';
import ImageViewer from 'builders/builderInternal/components/AITeacher/Viewer/ImageViewer';

const isPDF = (name) => name?.toLowerCase()?.endsWith('.pdf');
const isIMAGE = (name) =>
    ['.jpg', '.jpeg', '.png', '.gif', '.bmp', '.svg', '.webp'].some((ext) =>
        name?.toLowerCase()?.endsWith(ext)
    );
const isOFFICEFILE = (name) => {
    const exts = ['.ppt', '.pptx', '.doc', '.docx', '.xls', '.xlsx', '.csv'];
    return exts.some((ext) => name?.toLowerCase()?.endsWith(ext));
};
const isTextFile = (name) =>
    ['.txt', '.md', '.markdown'].some((ext) => name?.toLowerCase()?.endsWith(ext));

function TextViewer({ document }) {
    const [content, setContent] = useState('');
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);

    useEffect(() => {
        if (!document?.presigned_url) {
            setError('No URL available for this file.');
            setLoading(false);
            return;
        }
        setLoading(true);
        fetch(document.presigned_url)
            .then((res) => {
                if (!res.ok) throw new Error(`HTTP ${res.status}`);
                return res.text();
            })
            .then((text) => {
                setContent(text);
                setLoading(false);
            })
            .catch((err) => {
                setError(`Failed to load file: ${err.message}`);
                setLoading(false);
            });
    }, [document?.presigned_url]);

    if (loading) {
        return <p className="text-center text-neutral-400 p-4">Loading...</p>;
    }
    if (error) {
        return <p className="text-center text-red-400 p-4">{error}</p>;
    }
    return (
        <pre className="w-full h-full overflow-auto p-4 text-sm text-foreground whitespace-pre-wrap break-words font-mono">
            {content}
        </pre>
    );
}

export default function DocViewer({
    name,
    document,
    page,
}) {
    useEffect(() => {
        console.log('DocViewer Rendering:', {
            name,
            document,
            page,
        });
    }, [name, document, page]);

    const renderer = () => {
        if (!name) {
            return <p className="text-center text-neutral-400 p-4">No file name provided</p>;
        }

        if (!document) {
            return <p className="text-center text-neutral-400 p-4">No document selected</p>;
        }

        if (isPDF(name)) {
            return (
                <PDFViewerWithZoom
                    document={{
                        ...document,
                        name: document.document_name || name,
                    }}
                    page={page}
                />
            );
        }

        if (isTextFile(name)) {
            return <TextViewer document={document} />;
        }

        if (isOFFICEFILE(name)) {
            return (
                <OfficeViewer
                    document={{
                        ...document,
                        name: document.document_name || name,
                    }}
                    page={page}
                />
            );
        }

        if (isIMAGE(name)) {
            return (
                <ImageViewer
                    document={{
                        ...document,
                        name: document.document_name || name,
                    }}
                    page={page}
                />
            );
        }

        return (
            <p className="text-center text-neutral-400 p-4">
                No viewer for this file type: {name}
            </p>
        );
    };

    return <div className="h-full w-full">{renderer()}</div>;
}
